import * as React from 'react';
import { TextField as MUITextField, TextFieldProps, useTheme, IconButton, InputAdornment, Tooltip } from '@mui/material';
import { Control, Controller } from 'react-hook-form';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { tokens } from "../../theme";

export type InputFieldProps = {
    color?: "primary" | "secondary" | "success" | "error" | "info" | "warning";
    size?: "small" | "medium";
    label?: any | "";
    variant?: "filled" | "outlined" | "standard" | undefined;
    name?: string;
    control?: Control<any, any>;
    rules?: Object;
    isDisabled?: boolean;
    maxLength?: number;
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onBlur?: (event: React.FocusEventHandler<HTMLInputElement>) => void;
    controllerName?: string;
    isEmpty?: boolean;
    sx?: object;
} & TextFieldProps;

const CustomPasswordTextField: React.FC<InputFieldProps> = ({
    size,
    color,
    label,
    variant,
    name,
    control,
    rules,
    isDisabled,
    maxLength,
    onChange,
    onBlur,
    controllerName,
    isEmpty,
    sx,
    ...props
}) => {
    const theme = useTheme();
    const [showPassword, setShowPassword] = React.useState(false);
    const colors = tokens(theme.palette.mode);

    const togglePasswordVisibility = () => setShowPassword(prev => !prev);

    const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.target.value = e.target.value.replace(/\s/g, '');
    };

    const isRequired = () =>
        !!(rules && typeof rules === 'object' && 'required' in rules && label);

    const getLabel = () => {
        if (isRequired()) {
            return (
                <span>
                    {label}{" "}
                    <span style={{ color: colors.success[200], fontSize: "18px" }}>*</span>
                </span>
            );
        }
        return label;
    };

    // ── Match CustomTextField size behaviour ──────────────────────────────────
    const SIZE_STYLE_MAP = {
        small:  { height: 40, padding: "8px 12px" },
        medium: { height: 48, padding: "10px 14px" },
    };

    const internalSx = {
        "& .MuiOutlinedInput-root": {
            ...(size && {
                height: SIZE_STYLE_MAP[size ?? "medium"]?.height,
                "& input": {
                    padding: SIZE_STYLE_MAP[size ?? "medium"]?.padding,
                },
            }),
            "& fieldset": {
                borderColor: theme.palette.mode === "dark" ? "" : colors.primary[100],
            },
        },
        "& .MuiInputLabel-outlined": {
            color: theme.palette.mode === "dark" ? "" : colors.primary[100],
        },
    };

    return (
        <Controller
            name={controllerName || ""}
            control={control}
            rules={rules ?? {}}
            render={({ field, fieldState }) => (
                <MUITextField
                    {...field}
                    id={field.name}
                    name={name ?? controllerName}
                    label={getLabel() || ""}
                    fullWidth
                    type={showPassword ? 'text' : 'password'}
                    onChange={(e: any) => { field.onChange(e); onChange && onChange(e); }}
                    onInput={handleInput}
                    onBlur={(e: any) => { field.onBlur(); onBlur && onBlur(e); }}
                    size={size ?? "medium"}
                    color={color ?? (theme.palette.mode === "dark" ? "secondary" : "primary")}
                    variant={variant ?? "outlined"}
                    InputLabelProps={{
                        shrink: isDisabled ? true : Boolean(field.value),
                    }}
                    sx={[internalSx, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end" sx={{ marginRight: -1 }}>
                                <Tooltip
                                    title={showPassword ? "Hide password" : "Show password"}
                                    placement="top"
                                >
                                    <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={togglePasswordVisibility}
                                        edge="end"
                                        sx={{ padding: 0, margin: 0 }}
                                    >
                                        {showPassword ? <Visibility /> : <VisibilityOff />}
                                    </IconButton>
                                </Tooltip>
                            </InputAdornment>
                        ),
                        ...(maxLength ? { maxLength } : {}),
                        }}
                    error={!!fieldState.error}
                    helperText={fieldState.error ? fieldState.error.message : null}
                    disabled={isDisabled}
                    {...props}
                />
            )}
        />
    );
};

export default CustomPasswordTextField;